<!--
  OneDrive / SharePoint File Picker v8 (iframe, postMessage + MessagePort).
  Docs: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/?view=odsp-graph-online
  Schema: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/v8-schema
-->
<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import DrivesAPI from 'dashboard/api/drives';

const props = defineProps({
  driveId: {
    type: Number,
    required: true,
  },
  filterType: {
    type: String,
    default: null,
  },
  /** When true, only browse/navigate; no Select button (view-only, e.g. Drives overview) */
  viewerMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'cancel']);

const iframeContainer = ref(null);
const isLoading = ref(true);
const pickerError = ref(null);
const provider = ref(null);
const baseUrl = ref(null);
let messagePort = null;
let messageHandler = null;
let accessToken = null;
let channelId = null;

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function normalizePickedItem(item) {
  const endpoint = item['@sharePoint.endpoint'] || '';
  const driveId = item.parentReference?.driveId || '';
  const id = item.id || '';
  const webUrl =
    item.webUrl ||
    (endpoint && driveId && id
      ? `${endpoint}/drives/${driveId}/items/${id}`
      : '');
  const isPersonal = endpoint.includes('-my.');
  const sourceHint =
    provider.value === 'onedrive'
      ? 'personal'
      : isPersonal
        ? 'personal'
        : 'sharepoint';
  return {
    id: item.id,
    name: item.name || item.file?.name || 'File',
    web_url: webUrl,
    url: webUrl,
    provider: provider.value || 'onedrive',
    source_hint: sourceHint,
    graph_drive_id: driveId || undefined,
  };
}

async function handleAuthenticateCommand(msgId, command) {
  if (!messagePort) return;
  try {
    messagePort.postMessage({
      type: 'result',
      id: msgId,
      data: { result: 'token', token: accessToken },
    });
  } catch (err) {
    messagePort.postMessage({
      type: 'result',
      id: msgId,
      data: {
        result: 'error',
        error: {
          code: 'unableToObtainToken',
          message: err?.message || 'Failed to get token',
        },
      },
    });
  }
}

function handlePickCommand(msgId, command) {
  if (!messagePort) return;
  if (props.viewerMode) {
    messagePort.postMessage({
      type: 'result',
      id: msgId,
      data: {
        result: 'error',
        error: { code: 'viewOnly', message: 'Selection disabled' },
      },
    });
    return;
  }
  try {
    const items = command.items || [];
    const normalized = items.map(normalizePickedItem).filter(Boolean);
    if (normalized.length > 0) {
      emit('select', normalized.length === 1 ? normalized[0] : normalized);
    }
    messagePort.postMessage({
      type: 'result',
      id: msgId,
      data: { result: 'success' },
    });
  } catch (err) {
    messagePort.postMessage({
      type: 'result',
      id: msgId,
      data: {
        result: 'error',
        error: {
          code: 'unusableItem',
          message: err?.message || 'Failed to handle selection',
        },
      },
    });
  }
}

function handleCloseCommand() {
  emit('cancel');
}

function handleInitialize(event) {
  if (!iframeContainer.value?.querySelector('iframe')) return;
  const iframe = iframeContainer.value.querySelector('iframe');
  if (event.source !== iframe.contentWindow) return;

  const message = event.data;
  if (message?.type !== 'initialize' || message.channelId !== channelId) return;

  const port = event.ports?.[0];
  if (!port) return;

  messagePort = port;
  messageHandler = ev => {
    const payload = ev.data;
    if (payload.type === 'command') {
      port.postMessage({ type: 'acknowledge', id: payload.id });
      const cmd = payload.data;
      switch (cmd?.command) {
        case 'authenticate':
          handleAuthenticateCommand(payload.id, cmd);
          break;
        case 'pick':
          handlePickCommand(payload.id, cmd);
          break;
        case 'close':
          handleCloseCommand();
          break;
        default:
          port.postMessage({
            type: 'result',
            id: payload.id,
            data: {
              result: 'error',
              error: {
                code: 'unsupportedCommand',
                message: cmd?.command || 'unknown',
              },
            },
          });
      }
    }
  };
  port.addEventListener('message', messageHandler);
  port.start();
  port.postMessage({ type: 'activate' });
}

function loadPickerInIframe() {
  if (!iframeContainer.value || !accessToken || !channelId) return;

  const base = baseUrl.value?.replace(/\/$/, '') || '';
  if (!base) {
    pickerError.value = 'Missing base URL for picker';
    return;
  }

  const entry =
    provider.value === 'sharepoint'
      ? { sharePoint: { byPath: { web: base } } }
      : { oneDrive: {} };

  const options = {
    sdk: '8.0',
    entry,
    authentication: {},
    messaging: {
      origin: window.location.origin,
      channelId,
    },
  };

  if (props.viewerMode) {
    options.tray = {
      commands: [{ key: 'close', action: 'close', label: 'Close' }],
    };
  }

  const queryString = new URLSearchParams({
    filePicker: JSON.stringify(options),
    locale: 'en-us',
  });
  const actionUrl = `${base}/_layouts/15/FilePicker.aspx?${queryString.toString()}`;

  // Escape for use inside HTML double-quoted attributes (avoid cross-origin document access)
  const escapeHtmlAttr = str =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const iframe = document.createElement('iframe');
  iframe.setAttribute('name', `microsoft-picker-${channelId}`);
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.style.minHeight = '500px';
  iframe.style.border = 'none';
  iframeContainer.value.appendChild(iframe);

  // Use srcdoc so the form lives inside the iframe; the inline script submits it.
  // We never access iframe.contentWindow.document from the parent, avoiding SOP violations
  // when the iframe later navigates to SharePoint.
  const srcdoc = `<!DOCTYPE html><html><head></head><body>
<form id="pickerForm" action="${escapeHtmlAttr(actionUrl)}" method="POST">
<input type="hidden" name="access_token" value="${escapeHtmlAttr(accessToken)}" />
</form>
<script>document.getElementById('pickerForm').submit();<\/script>
</body></html>`;
  iframe.srcdoc = srcdoc;
}

async function initPicker() {
  if (!props.driveId) return;

  isLoading.value = true;
  pickerError.value = null;
  try {
    const res = await DrivesAPI.getPickerConfig(props.driveId);
    const data = res.data;
    if (!data?.success || !data?.access_token) {
      throw new Error(data?.error || 'Failed to get picker config');
    }
    if (!data.base_url && data.provider !== 'google') {
      throw new Error('Missing base URL for Microsoft picker');
    }
    accessToken = data.access_token;
    provider.value = data.provider;
    baseUrl.value = data.base_url;
    channelId = uuid();

    window.addEventListener('message', handleInitialize);
    // Iframe container ref is only in DOM when isLoading is false; defer so ref is set
    isLoading.value = false;
    await nextTick();
    loadPickerInIframe();
  } catch (err) {
    pickerError.value = err?.message || 'Failed to load Microsoft picker';
    useAlert(pickerError.value);
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  initPicker();
});

onUnmounted(() => {
  window.removeEventListener('message', handleInitialize);
  if (messagePort && messageHandler) {
    messagePort.removeEventListener('message', messageHandler);
  }
});
</script>

<template>
  <div class="w-full h-full flex flex-col min-h-[500px]">
    <div
      v-if="isLoading"
      class="flex items-center justify-center min-h-[500px]"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"
        />
        <p class="text-sm text-slate-600 dark:text-slate-400">
          Loading Microsoft file picker...
        </p>
      </div>
    </div>

    <div
      v-else-if="pickerError"
      class="flex flex-col items-center justify-center min-h-[500px] p-6 text-center"
    >
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ pickerError }}
      </p>
      <button
        type="button"
        class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        @click="initPicker"
      >
        Retry
      </button>
    </div>

    <div v-else ref="iframeContainer" class="flex-1 w-full min-h-[500px]" />
  </div>
</template>
